import {applyMiddleware, combineReducers, compose, createStore} from 'redux';
import {composeWithDevTools} from 'redux-devtools-extension';
import reduxThunk from 'redux-thunk';

import {connectRouter, routerMiddleware} from './stores/connected-react-router';

import apiMiddleware from './middleware/api';
import externalMiddleware from './middleware/external-calls';
import socketIOMiddleware from './middleware/sockets';

import type {ViewerStore} from 'components/viewer/viewer-reducer';
import type {History} from 'history';
import type {DefaultRootState} from 'react-redux';
import type {Action, Reducer, ReducersMapObject, Store} from 'redux';
import type {AppStore} from 'stores/app-store';
import type {IntlStore} from 'stores/intl-store';
import type {ModalsStore} from 'stores/modals-store';
import type {SessionStore} from 'stores/session-store';
import type {PublicSessionViewerStore} from 'views/authorized-anonymous/stores/public-session-viewer-store';
import type {ScenarioDetailStore} from 'views/signed-in/scenarios/stores/scenario-detail-store';
import type {ScenarioImportStore} from 'views/signed-in/scenarios/stores/scenario-import-store';
import type {ScenariosStore} from 'views/signed-in/scenarios/stores/scenarios-store';
import type {LiveReservationSelectionStore} from 'views/signed-in/sessions/stores/live-reservation-selection-store';
import type {LiveSessionStore} from 'views/signed-in/sessions/stores/live-session-store';
import type {SessionImportStore} from 'views/signed-in/sessions/stores/session-import-store';
import type {SessionViewerStore} from 'views/signed-in/sessions/stores/session-viewer-store';
import type {SessionBulkUpdateStore} from 'views/signed-in/sessions/stores/sessions-bulk-update-store';
import type {SessionsStore} from 'views/signed-in/sessions/stores/sessions-store';
import type {SsAnnotationsStore} from 'views/signed-in/system-setup/stores/ss-annotations-store';
import type {SsGeneralStore} from 'views/signed-in/system-setup/stores/ss-general-store';
import type {SSLibrariesStore} from 'views/signed-in/system-setup/stores/ss-libraries-store';
import type {SsUsersSecurityStore} from 'views/signed-in/system-setup/stores/ss-users-security-store';
import type {UserDetailStore} from 'views/signed-in/users/stores/user-detail-store';
import type {UserListStore} from 'views/signed-in/users/stores/user-list-store';

const GlobalReducer: ReducersMapObject = {};

export let store: Store<DefaultRootState>;

type ActionCallback<S> = (state?: S, action?: Action) => void | Partial<S>;

export function createReducer<S>(initialState: S, actionsMap: {[key: string]: ActionCallback<S>}): Reducer<S> {
    return (state: S = initialState, action: Action): S => {
        const reduceFn = actionsMap[action.type];
        if (!reduceFn) {
            return state;
        }

        return Object.assign<Partial<S>, S, Partial<S>>(
            {},
            state,
            // @ts-expect-error TS2345: Argument of type void | Partial<S> is not assignable to parameter of type Partial<S>
            // Type void is not assignable to type Partial<S>
            reduceFn(state, action),
        );
    };
}

export function connectReducer<S>(name: string, reducer: Reducer<S>): void {
    GlobalReducer[name] = reducer;

    if (!store) {
        return;
    }
    const newReducer = combineReducers<DefaultRootState>(GlobalReducer);
    store.replaceReducer(newReducer);
}

export function initializeReduxStore<HistoryLocationState>(history: History<HistoryLocationState>) {
    const routerSyncMiddleware = routerMiddleware(history);
    const middlewares = [routerSyncMiddleware, reduxThunk, apiMiddleware, externalMiddleware, socketIOMiddleware];
    const deployment = __DEPLOYMENT__;

    let enhancer = applyMiddleware(...middlewares);
    /**
     * @todo    SCLD-14919 deployment should never be undefined. It currently is
     *          undefined when running unit tests. Remove this temppoary fix.
     */
    if (deployment === undefined) {
        // do nothing. Remove this check.
    } else if (!deployment.includes('production')) {
        enhancer = composeWithDevTools(enhancer);
    } else {
        enhancer = compose(enhancer);
    }

    connectReducer('router', connectRouter(history));
    const reducer = combineReducers<DefaultRootState>(GlobalReducer);

    store = createStore<DefaultRootState>(reducer, enhancer);
    return store;
}

declare module 'react-redux' {
    export interface DefaultRootState {
        app: AppStore;
        intl: IntlStore;
        live: LiveSessionStore;
        liveReservationSelection: LiveReservationSelectionStore;
        modals: ModalsStore;
        publicSessionViewer: PublicSessionViewerStore;
        scenarioDetail: ScenarioDetailStore;
        scenarioImport: ScenarioImportStore;
        scenarios: ScenariosStore;
        session: SessionStore;
        sessionsBulkUpdate: SessionBulkUpdateStore;
        sessionImport: SessionImportStore;
        sessions: SessionsStore;
        sessionViewer: SessionViewerStore;
        ssAnnotations: SsAnnotationsStore;
        ssGeneral: SsGeneralStore;
        SSLibrariesStore: SSLibrariesStore;
        ssUsersSecurity: SsUsersSecurityStore;
        userDetail: UserDetailStore;
        userList: UserListStore;
        viewer: ViewerStore;
    }
}
